import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { getUserId, getToken } from "../../utilities/authHelper";
import { validatePassword } from "../../utilities/CommonUtilities";
import FloatingLabel from "react-bootstrap/FloatingLabel";

function PasswordChangeModal({ show, handleClose }) {
  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errorMessage, setErrorMessage] = useState([]);
  const apiUrl = process.env.REACT_APP_API_URL;
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [visibility, setVisibility] = useState({
    currentPassword: true,
    newPassword: true,
  });

  useEffect(() => {
    if (!show) {
      setPasswords({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setErrorMessage([]);
    }
  }, [show]);

  const handleChange = (e) => {
    setPasswords({ ...passwords, [e.target.name]: e.target.value });
  };

  const toggleVisibility = (field) => {
    setVisibility({ ...visibility, [field]: !visibility[field] });
  };

  const handleCloseSuccessModal = () => setShowSuccessModal(false);

  useEffect(() => {
    if (showSuccessModal) {
      const timer = setTimeout(() => {
        handleCloseSuccessModal();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccessModal]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);

    const { currentPassword, newPassword, confirmPassword } = passwords;

    const passwordError = validatePassword(newPassword);
    if (passwordError.length > 0 || newPassword !== confirmPassword) {
      setErrorMessage(
        passwordError.length > 0 ? passwordError : ["Passwords don't match."]
      );
      setIsLoading(false);
      return;
    }

    const user_id = getUserId();
    const token = getToken();

    if (!user_id || !token) {
      setErrorMessage(["Unable to authenticate. Please log in again."]);
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/auth:change_password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
        body: JSON.stringify({
          user_id,
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        setErrorMessage([`Reason: ${data.detail}`]);
      } else {
        setPasswords({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
        handleClose();
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error("Password change failed:", error);
      setErrorMessage(["Password change failed. Please try again."]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal
        show={show && !showSuccessModal}
        onHide={handleClose}
        dialogClassName="modal-50w"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Change Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3 ps-2 pe-2" controlId="currentPassword">
              <FloatingLabel
                controlId="floatingInputcurrentPassword"
                label="Enter current password"
                className="mb-3"
              >
                <Form.Control
                  type={visibility.currentPassword ? "text" : "password"}
                  placeholder="Enter current password"
                  name="currentPassword"
                  value={passwords.currentPassword}
                  onChange={handleChange}
                  disabled={isLoading}
                  style={{ paddingRight: "2.5rem" }} // To avoid overlap of the icon
                />
                <i
                  className={`position-absolute top-50 end-0 translate-middle-y me-2 cursor-pointer ${
                    visibility.currentPassword
                      ? "fas fa-eye-slash"
                      : "fas fa-eye"
                  }`}
                  onClick={() => toggleVisibility("currentPassword")}
                  style={{ zIndex: 10, cursor: "pointer" }}
                ></i>
              </FloatingLabel>
            </Form.Group>

            <Form.Group className="mb-3 ps-2 pe-2" controlId="newPassword">
              <FloatingLabel
                controlId="floatingInputnewPassword"
                label="Enter new password"
                className="mb-3"
              >
                <Form.Control
                  type={visibility.newPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  name="newPassword"
                  value={passwords.newPassword}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  style={{ paddingRight: "2.5rem" }} // To avoid overlap of the icon
                />
                <i
                  className={`position-absolute top-50 end-0 translate-middle-y me-2 cursor-pointer ${
                    visibility.newPassword ? "fas fa-eye-slash" : "fas fa-eye"
                  }`}
                  onClick={() => toggleVisibility("newPassword")}
                  style={{ zIndex: 10, cursor: "pointer" }}
                ></i>
              </FloatingLabel>
            </Form.Group>

            <Form.Group className="mb-3 ps-2 pe-2" controlId="confirmPassword">
              <FloatingLabel
                controlId="floatingInputconfirmPassword"
                label="Confirm new password"
                className="mb-3"
              >
                <Form.Control
                  type="password"
                  placeholder="Confirm new password"
                  name="confirmPassword"
                  value={passwords.confirmPassword}
                  onChange={handleChange}
                  disabled={isLoading}
                />
              </FloatingLabel>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          {errorMessage.length > 0 && (
            <div className="alert alert-danger">
              {errorMessage.map((msg, index) => (
                <React.Fragment key={index}>
                  {msg} {index < errorMessage.length - 1 && <br />}
                </React.Fragment>
              ))}
            </div>
          )}
          {isLoading ? (
            <Button variant="primary" disabled>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
              <span className="ms-2">Loading...</span>
            </Button>
          ) : (
            <Button variant="primary" type="submit" onClick={handleSubmit}>
              Save Changes
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      <Modal show={showSuccessModal} onHide={handleCloseSuccessModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Password Changed</Modal.Title>
        </Modal.Header>
        <Modal.Body>Your password has been successfully changed.</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCloseSuccessModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default PasswordChangeModal;
